import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

export const LANGUAGES = [
  { code: "nl", label: "Nederlands" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
];

// Compact language selector — mirrors the Virtue Guide app pattern:
// current language as a pill, full native names in the dropdown list.
export default function LanguageSelector({ prefix = "lang", align = "right", onSelect }) {
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={`${prefix}-toggle`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.switchTo")}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase font-medium pl-3 pr-2 py-1.5 bg-black/70 border border-gold/60 rounded-full text-bone/90 hover:border-gold transition-colors backdrop-blur-sm"
      >
        <span className="text-gold">{lang.toUpperCase()}</span>
        <ChevronDown size={12} strokeWidth={2} aria-hidden
          className={`text-bone/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t("lang.switchTo")}
          data-testid={`${prefix}-menu`}
          className={`absolute z-[70] mt-2 min-w-[11rem] py-1 bg-black/95 backdrop-blur-xl border border-gold/30 shadow-[0_8px_32px_rgba(0,0,0,0.6)] ${align === "right" ? "right-0" : "left-0"}`}
        >
          {LANGUAGES.map((l) => (
            <li key={l.code} role="option" aria-selected={lang === l.code}>
              <button
                type="button"
                data-testid={`${prefix}-option-${l.code}`}
                onClick={() => { setLang(l.code); setOpen(false); onSelect && onSelect(l.code); }}
                className={`w-full flex items-center justify-between gap-6 px-4 py-2.5 text-left text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  lang === l.code ? "text-gold" : "text-bone/75 hover:text-bone hover:bg-white/5"
                }`}
              >
                <span className="normal-case text-[13px] tracking-[0.06em]">{l.label}</span>
                {lang === l.code && <Check size={13} strokeWidth={2} aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
